<template>
  <!--
    Komponent wyboru zakresu dat z pickerem.
    Umożliwia użytkownikowi ustawienie daty początkowej i końcowej.
    Wyświetla tytuł oraz dwa pickery dla "Od" i "Do".
  -->
  <div class="date-range ds-panel">
    <!--
      Nagłówek panelu z dynamiczną klasą koloru, jeśli są błędy wymaganych pól.
    -->
    <div class="ds-panel__head">
      <h3
        :class="{
          'text-danger':
            this.store.error &&
            (this.store.requireFields.includes('dateTimeStart') ||
              this.store.requireFields.includes('dateTimeEnd')),
        }"
        class="ds-panel__title"
      >
        {{ t('date-range').toUpperCase() }}
      </h3>
    </div>

    <!-- Dwa pola zakresu: data początkowa i końcowa, każde z własną etykietą.

         `teleport` przenosi kalendarz do `<body>`. Bez tego renderuje się
         wewnątrz `.ds-panel`, który ma `overflow: hidden` (potrzebne, żeby
         zaokrąglone narożniki obcinały tabele) — kalendarz był przycinany do
         panelu i wyglądał, jakby się nie otwierał; widoczny zostawał tylko
         dzióbek nad dolną krawędzią panelu. -->
    <div class="ds-panel__body date-range__body">
      <div class="date-range__field">
        <span class="ds-field-label">{{ t('from') }}</span>
        <VueDatePicker
          v-model="fromDate"
          time-picker-inline
          :format="fromFormat"
          :month-change-on-scroll="false"
          locale="pl"
          :cancel-text="t('cancel')"
          :select-text="t('select')"
          inputClassName="daterange-input"
          teleport="body"
          :teleport-center="compactDatePicker"
          :menu-class-name="datePickerMenuClass"
          :disabled="store.disabledFields?.dateFrom"
        ></VueDatePicker>
      </div>

      <div class="date-range__field">
        <span class="ds-field-label">{{ t('to') }}</span>
        <VueDatePicker
          v-model="toDate"
          time-picker-inline
          :format="toFormat"
          :month-change-on-scroll="false"
          locale="pl"
          :cancel-text="t('cancel')"
          :select-text="t('select')"
          inputClassName="daterange-input"
          teleport="body"
          :teleport-center="compactDatePicker"
          :menu-class-name="datePickerMenuClass"
          :disabled="store.disabledFields?.dateTo"
        ></VueDatePicker>
      </div>
    </div>
  </div>
</template>

<script>
import { watch, ref } from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import { useCompactDatePicker } from "@/utils/datePickerLayout";
// CSS biblioteki ładowany globalnie w `main.js` (kolejność kaskady) — nie importuj go tutaj.

export default {
  name: "DateRange",  // Nazwa komponentu
  props: {
    store: { required: true },       // Pinia store z filtrami dat
    dateTimeStart: { required: false },
    dateTimeEnd: { required: false },
  },
  components: { VueDatePicker },
  setup(props) {
    // Na niskich/wąskich ekranach kalendarz otwiera się wyśrodkowany, bo popup
    // „przy polu" nie mieścił się w viewporcie (patrz `utils/datePickerLayout`).
    const { compactDatePicker, datePickerMenuClass } = useCompactDatePicker();

    // inicjalizacja wartości dla pickerów na podstawie store
    const fromDate = ref(
      props.store.filters.dateTimeStart
        ? props.store.filters.dateTimeStart
        : null
    );
    const toDate = ref(
      props.store.filters.dateTimeEnd ? props.store.filters.dateTimeEnd : null
    );

    // Format wyświetlanej daty początkowej
    const fromFormat = (date) => {
      return `Od ${(date.getDate()<10?"0":"")+date.getDate()}.${
        (date.getMonth()+1<10?"0":"")+(date.getMonth()+1)
      }.${date.getFullYear()},  ${date.getHours()}:${
        (date.getMinutes()<10?"0":"")+date.getMinutes()
      }`;
    };
    // Format wyświetlanej daty końcowej
    const toFormat = (date) => {
      return `Do ${(date.getDate()<10?"0":"")+date.getDate()}.${
        (date.getMonth()+1<10?"0":"")+(date.getMonth()+1)
      }.${date.getFullYear()},  ${date.getHours()}:${
        (date.getMinutes()<10?"0":"")+date.getMinutes()
      }`;
    };

    // Helper do zapisu wartości do store
    const saveValue = (fieldName, text) => {
      props.store.addItem(fieldName, text);
    };

    // Watcher monitorujący zmiany obu pickerów
    watch(
      [fromDate, toDate],
      ([newFromDate, newToDate], [prevFromDate, prevToDate]) => {
        // Obsługa zmiany daty początkowej
        if (newFromDate !== prevFromDate) {
          if (fromDate.value === null) {
            // usunięcie filtra z store, jeśli wyczyszczono
            delete props.store.filters.dateTimeStart;
          } else {
            // zapis do store i korekta daty końcowej, jeśli wcześniejsza
            saveValue(
              "dateTimeStart",
              new Date(fromDate.value).toISOString()
            );
            if (fromDate.value > toDate.value) {
              toDate.value = new Date(
                new Date(fromDate.value).setDate(
                  new Date(fromDate.value).getDate() + 1
                )
              );
            }
          }
        }
        // Obsługa zmiany daty końcowej
        if (newToDate !== prevToDate) {
          if (toDate.value === null) {
            delete props.store.filters.dateTimeEnd;
          } else {
            saveValue("dateTimeEnd", new Date(toDate.value).toISOString());
            if (toDate.value < fromDate.value) {
              fromDate.value = new Date(
                new Date(toDate.value).setDate(
                  new Date(toDate.value).getDate() - 1
                )
              );
            }
          }
        }
      }
    );

    return {
      fromDate,
      toDate,
      fromFormat,
      toFormat,
      compactDatePicker,
      datePickerMenuClass,
    };
  },
  watch: {
    // Synchronizacja propów z lokalnym refem
    dateTimeStart(newVal) {
      this.fromDate = newVal;
    },
    dateTimeEnd(newVal) {
      this.toDate = newVal;
    },
  },
  created() {
    // Watchery na zmiany store — reagują na usunięcie wartości oraz na zmianę
    // ustawioną z zewnątrz (np. odświeżenie daty "do" przy powrocie na krok 1).
    this.$watch(
      () => this.store.filters.dateTimeStart,
      (val) => {
        if (!val) {
          this.fromDate = null;
          return;
        }
        if (this.toIso(this.fromDate) !== this.toIso(val)) this.fromDate = val;
      }
    );
    this.$watch(
      () => this.store.filters.dateTimeEnd,
      (val) => {
        if (!val) {
          this.toDate = null;
          return;
        }
        if (this.toIso(this.toDate) !== this.toIso(val)) this.toDate = val;
      }
    );
  },
  methods: {
    // Porównanie wartości pickera (Date lub string ISO) ze store
    toIso(val) {
      if (!val) return null;
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d.toISOString();
    },
  },
};
</script>

<style scoped>
/* Panel „Zakres dat" — dwa pola z pickerem, jedno pod drugim. */
.date-range {
  min-width: 0;
}

.date-range__body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.date-range__field {
  min-width: 0;
}
</style>

<style>
/* Wysokość i obramowanie pola datepickera (renderowanego przez bibliotekę)
   dopasowane do pozostałych kontrolek formularza. */
.date-range .dp__input {
  min-height: var(--control-height);
  padding-left: 35px;
  border: 1px solid var(--border-default);
  border-radius: var(--control-radius);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
}

.date-range .dp__input:hover {
  border-color: var(--border-strong);
}

.date-range .dp__input_focus {
  border-color: var(--border-focus);
  box-shadow: 0 0 0 2px var(--accent-focus-ring);
}
</style>
